import { memo, useCallback } from "react";
import { PressableScale } from "pressto";
import { View, Text } from "@src/components";
import { useNumDays, type GridStore } from "../grid-store";
import { type Granularity, getSlotsPerDay } from "../granularity-utils";
import { useUnistyles } from "react-native-unistyles";

const CycleLengthLabel = memo(() => (
  <Text textStyle="sm/regular" color="textSecondary">
    Length of cycle
  </Text>
));

const CycleLengthControl = memo<{ store: GridStore }>(({ store }) => {
  const { theme } = useUnistyles();
  const numDays = useNumDays(store);

  const addDay = useCallback(() => {
    store.getState().setNumDays(numDays + 1);
  }, [store, numDays]);

  const removeDay = useCallback(() => {
    if (numDays > 1) {
      const { cells, initialCells, granularity } = store.getState();
      const newCells = { ...cells };
      const newInitialCells = { ...initialCells };
      const slots = getSlotsPerDay(granularity as Granularity);
      for (let slot = 0; slot < slots; slot++) {
        const key = `${numDays - 1}-${slot}`;
        delete newCells[key];
        delete newInitialCells[key];
      }
      store.setState({ cells: newCells, initialCells: newInitialCells, numDays: numDays - 1 });
    }
  }, [store, numDays]);

  return (
    <View flexDirection="row" alignItems="center">
      <PressableScale onPress={removeDay} enabled={numDays > 1}>
        <View
          style={{
            opacity: numDays <= 1 ? 0.5 : 1,
            paddingHorizontal: theme.spacing["2xl"],
            paddingVertical: theme.spacing.lg,
            borderWidth: 1,
            borderRightWidth: 0,
            borderColor: theme.colors.field.border_default,
            backgroundColor: theme.colors.surface1.background,
            borderTopLeftRadius: theme.spacing.xl,
            borderBottomLeftRadius: theme.spacing.xl,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text textStyle="sm/regular">-</Text>
        </View>
      </PressableScale>
      <View
        style={{
          paddingHorizontal: theme.spacing["2xl"],
          paddingVertical: theme.spacing.lg,
          borderWidth: 1,
          borderColor: theme.colors.interactive.buttons.default_background,
          backgroundColor: theme.colors.surface2.background,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Text textStyle="sm/regular" color="text">
          {numDays} {numDays === 1 ? "Day" : "Days"}
        </Text>
      </View>
      <PressableScale onPress={addDay}>
        <View
          style={{
            paddingHorizontal: theme.spacing["2xl"],
            paddingVertical: theme.spacing.lg,
            borderWidth: 1,
            borderLeftWidth: 0,
            borderColor: theme.colors.field.border_default,
            backgroundColor: theme.colors.surface1.background,
            borderTopRightRadius: theme.spacing.xl,
            borderBottomRightRadius: theme.spacing.xl,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text textStyle="sm/regular">+</Text>
        </View>
      </PressableScale>
    </View>
  );
});

export const CycleLengthSection = memo<{ store: GridStore }>(({ store }) => (
  <View flexDirection="row" gap="md" pb="lg" alignItems="center" justifyContent="space-between">
    <CycleLengthLabel />
    <CycleLengthControl store={store} />
  </View>
));
